/**
 * KV/IndexedDB Purchases Repository (ADR-0009 Phase 2b).
 */

import {
  persistPurchasesAndProducts,
  queueBusinessPayloadFieldWrite,
  queueBusinessPayloadWrite
} from '../storage/businessPayloadWrite';
import { publishDomainEvent, DOMAIN_EVENTS } from '../events/domainEventBus';
import {
  enqueueOutbox,
  normalizeOutboxBusinessId,
  buildPurchaseSyncOutboxEntry,
  purchaseOutboxId
} from '../storage/outboxAccess';
import { isDexieAtomicBusinessWriteEnabled } from '../storage/dexieBusinessTransaction';
import {
  readPurchasesFromBestSource,
  refreshPurchasesReadCache
} from '../../utils/purchasesReadSource';

function normalizePurchasesArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return Object.values(raw);
  return [];
}

export class KvPurchasesRepository {
  /**
   * @param {string} businessId
   * @param {{ purchases: Array, products?: Array }} payload
   * @param {{ syncPurchases?: Array }} [options]
   * @returns {Promise<import('../../application/ports/purchasesRepository.port').SavePurchasesResult>}
   */
  async savePurchasesAndProducts(businessId, payload, options = {}) {
    const syncPurchases = options.syncPurchases || [];
    const outboxEntries = this._buildPurchaseOutboxEntries(businessId, syncPurchases);
    const result = await persistPurchasesAndProducts(businessId, payload, {
      outboxEntries: isDexieAtomicBusinessWriteEnabled() ? outboxEntries : undefined
    });
    await refreshPurchasesReadCache(businessId);
    const outboxIds = isDexieAtomicBusinessWriteEnabled()
      ? outboxEntries.map((entry) => entry.id)
      : await this._enqueuePurchaseSync(businessId, syncPurchases);
    return { ...result, outboxIds };
  }

  /**
   * @param {string} businessId
   * @param {object} purchase
   * @param {Array} [products]
   */
  async appendPurchase(businessId, purchase, products) {
    let finalPurchases = [];
    const outboxEntries = this._buildPurchaseOutboxEntries(businessId, [purchase]);
    await queueBusinessPayloadWrite(
      businessId,
      (parsed) => {
        const purchases = normalizePurchasesArray(parsed.purchases);
        finalPurchases = [...purchases, purchase];
        parsed.purchases = finalPurchases;
        if (products) parsed.products = products;
        return parsed;
      },
      {
        authoritativeFields: ['purchases', ...(products ? ['products'] : [])],
        outboxEntries: isDexieAtomicBusinessWriteEnabled() ? outboxEntries : undefined
      }
    );
    const outboxIds = isDexieAtomicBusinessWriteEnabled()
      ? outboxEntries.map((entry) => entry.id)
      : await this._enqueuePurchaseSync(businessId, [purchase]);
    await refreshPurchasesReadCache(businessId);
    publishDomainEvent(DOMAIN_EVENTS.PURCHASES_UPDATED, {
      businessId,
      purchases: finalPurchases,
      products: products ?? undefined
    });
    return {
      purchases: finalPurchases,
      products: products ?? undefined,
      outboxIds
    };
  }

  /**
   * @param {string} businessId
   */
  async listPurchases(businessId) {
    return readPurchasesFromBestSource(businessId);
  }

  /**
   * Ersetzt die gesamte purchases-Liste (z. B. nach Löschen in der UI).
   * @param {string} businessId
   * @param {Array} purchases
   */
  async replacePurchases(businessId, purchases) {
    const next = Array.isArray(purchases) ? purchases : [];
    await queueBusinessPayloadFieldWrite(businessId, 'purchases', next);
    await refreshPurchasesReadCache(businessId);
    return next;
  }

  _buildPurchaseOutboxEntries(businessId, purchases) {
    const outboxEntries = [];
    for (const purchase of purchases) {
      const entry = buildPurchaseSyncOutboxEntry(businessId, purchase);
      if (entry) outboxEntries.push(entry);
    }
    return outboxEntries;
  }

  async _enqueuePurchaseSync(businessId, purchases) {
    return enqueuePurchasesForCloudSync(businessId, purchases, { reason: 'save' });
  }
}

/**
 * Enqueue purchase_sync intents (create, update, soft-void).
 * Outbox id = purchaseOutboxId (ADR-0017 W2/W3 — same as bootstrap).
 * @param {string} businessId
 * @param {object[]} purchases
 * @param {{ reason?: string }} [options]
 */
export async function enqueuePurchasesForCloudSync(businessId, purchases, options = {}) {
  const list = Array.isArray(purchases) ? purchases : [];
  const outboxIds = [];
  for (const purchase of list) {
    const entry = buildPurchaseSyncOutboxEntry(businessId, purchase);
    if (!entry) continue;
    try {
      const row = await enqueueOutbox(entry);
      outboxIds.push(row?.id ?? entry.id);
    } catch (outboxErr) {
      console.warn(
        '[purchase_sync] outbox enqueue failed:',
        outboxErr?.message || outboxErr
      );
    }
  }
  if (outboxIds.length > 0) {
    void import('../../services/parallelCloudFieldSync')
      .then(({ scheduleAutomaticCloudSync }) => {
        scheduleAutomaticCloudSync(businessId, {
          reason: options.reason || 'save'
        });
      })
      .catch(() => {});
  }
  return outboxIds;
}

// Re-export for call sites that only need id helpers
export { purchaseOutboxId, normalizeOutboxBusinessId };
